import { useState, useEffect, useCallback } from 'react';
import { rmmAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Loader2, Server, Boxes, Network, Globe, ShieldAlert, Building2, Wrench } from 'lucide-react';
import { toast } from 'sonner';
import { fmtWhen, fmtUptime, pctColor } from './rmmUtils';

const Spin = () => <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-cyan-500" /></div>;

const Stat = ({ icon: Icon, label, value, sub, testid, tone = 'text-cyan-600' }) => (
  <Card className="bg-white border-slate-200" data-testid={testid}>
    <CardContent className="p-4 flex items-center gap-3">
      <div className="p-2.5 rounded-xl bg-slate-100"><Icon className={`w-5 h-5 ${tone}`} /></div>
      <div>
        <p className="text-2xl font-bold text-slate-800 leading-none">{value}</p>
        <p className="text-xs text-slate-500 mt-1">{label}{sub ? ` · ${sub}` : ''}</p>
      </div>
    </CardContent>
  </Card>
);

export const InfrastructureTab = ({ isAdmin }) => {
  const [data, setData] = useState(null);
  const [editSvc, setEditSvc] = useState(null); // {id, hostname, value}

  const load = useCallback(() => {
    rmmAPI.infraSummary().then((r) => setData(r.data)).catch(() => setData({ servers: [], websites: [], sites_health: [], cert_warnings: [] }));
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  if (!data) return <Spin />;
  const hosts = data.servers.filter((s) => (s.hyperv || {}).is_host);

  const saveServices = () => {
    const services = editSvc.value.split(',').map((s) => s.trim()).filter(Boolean);
    rmmAPI.setMonitoredServices(editSvc.id, services)
      .then(() => { toast.success('Watched services updated'); setEditSvc(null); load(); })
      .catch((e) => toast.error(e.response?.data?.detail || 'Failed'));
  };

  return (
    <div className="space-y-6" data-testid="rmm-infrastructure-tab">
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <Stat icon={Server} label="Servers" value={data.servers_total} sub={`${data.servers_online} online`} testid="infra-stat-servers" />
        <Stat icon={Boxes} label="Hyper-V VMs" value={data.vms_total} sub={`${data.vms_running} running`} testid="infra-stat-vms" tone="text-violet-600" />
        <Stat icon={Network} label="Network devices" value={data.network_devices_total} sub={`${data.network_devices_online} online`} testid="infra-stat-network" tone="text-blue-600" />
        <Stat icon={Globe} label="Websites" value={data.websites.length} sub={`${data.websites_up} up`} testid="infra-stat-websites" tone="text-emerald-600" />
        <Stat icon={ShieldAlert} label="Cert warnings" value={data.cert_warnings.length} testid="infra-stat-certs" tone={data.cert_warnings.length ? 'text-red-600' : 'text-slate-400'} />
        <Stat icon={Building2} label="Monitored sites" value={data.sites_health.length} testid="infra-stat-sites" tone="text-cyan-600" />
      </div>

      <Card className="bg-white border-slate-200">
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Server className="w-4 h-4 text-cyan-500" />Windows Servers</CardTitle></CardHeader>
        <CardContent>
          {data.servers.length === 0 ? <p className="text-sm text-slate-500 py-4 text-center">No servers enrolled yet — enroll a Windows Server with the RMM agent.</p> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs text-slate-500 border-b border-slate-200">
                  <th className="py-2 pr-3">Server</th><th className="pr-3">Customer</th><th className="pr-3">Status</th>
                  <th className="pr-3">CPU</th><th className="pr-3">RAM</th><th className="pr-3">Disk</th><th className="pr-3">Uptime</th>
                  <th className="pr-3">Watched services</th><th className="pr-3">Last seen</th></tr></thead>
                <tbody>
                  {data.servers.map((s) => (
                    <tr key={s.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`infra-server-row-${s.hostname}`}>
                      <td className="py-2 pr-3 font-medium text-slate-800">{s.hostname}{(s.hyperv || {}).is_host && <Badge className="ml-1.5 bg-violet-100 text-violet-700 text-[10px]">Hyper-V</Badge>}</td>
                      <td className="pr-3 text-slate-600">{s.customer_name || '—'}</td>
                      <td className="pr-3"><Badge className={s.status === 'online' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>{s.status}</Badge></td>
                      <td className={`pr-3 ${pctColor(s.cpu_percent)}`}>{s.cpu_percent != null ? `${Math.round(s.cpu_percent)}%` : '—'}</td>
                      <td className={`pr-3 ${pctColor(s.ram_percent)}`}>{s.ram_percent != null ? `${Math.round(s.ram_percent)}%` : '—'}</td>
                      <td className={`pr-3 ${pctColor(s.disk_percent)}`}>{s.disk_percent != null ? `${Math.round(s.disk_percent)}%` : '—'}</td>
                      <td className="pr-3 text-slate-600">{fmtUptime(s.uptime_seconds)}</td>
                      <td className="pr-3">
                        {editSvc?.id === s.id ? (
                          <span className="flex gap-1 items-center">
                            <Input value={editSvc.value} onChange={(e) => setEditSvc({ ...editSvc, value: e.target.value })} className="h-7 text-xs w-52" data-testid="infra-services-input" placeholder="MSSQLSERVER, Spooler" />
                            <Button size="sm" className="h-7 px-2 bg-cyan-600" onClick={saveServices} data-testid="infra-services-save-btn">Save</Button>
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 flex-wrap">
                            {(s.services_down || []).map((n) => <Badge key={n} className="bg-red-100 text-red-700 text-[10px]" data-testid={`infra-service-down-${n}`}>{n} down</Badge>)}
                            {(s.monitored_services || []).filter((n) => !(s.services_down || []).includes(n)).map((n) => <Badge key={n} className="bg-emerald-100 text-emerald-700 text-[10px]">{n}</Badge>)}
                            {!(s.monitored_services || []).length && <span className="text-xs text-slate-400">none</span>}
                            {isAdmin && <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-slate-400" data-testid={`infra-services-edit-${s.hostname}`}
                              onClick={() => setEditSvc({ id: s.id, value: (s.monitored_services || []).join(', ') })}><Wrench className="w-3 h-3" /></Button>}
                          </span>
                        )}
                      </td>
                      <td className="pr-3 text-xs text-slate-500">{fmtWhen(s.last_seen)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {hosts.length > 0 && (
        <Card className="bg-white border-slate-200">
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Boxes className="w-4 h-4 text-violet-500" />Hyper-V Hosts & Virtual Machines</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {hosts.map((h) => (
              <div key={h.id} className="border border-slate-200 rounded-xl p-3" data-testid={`infra-hyperv-${h.hostname}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-slate-800">{h.hostname}</span>
                  <Badge className={h.status === 'online' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>{h.status}</Badge>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {((h.hyperv || {}).vms || []).map((vm) => {
                    const run = String(vm.state || '').toLowerCase() === 'running';
                    return <Badge key={vm.name} className={`${run ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'}`} data-testid={`infra-vm-${vm.name}`}>
                      {vm.name} · {vm.state}{run && vm.cpu_percent != null ? ` · ${vm.cpu_percent}% CPU` : ''}
                    </Badge>;
                  })}
                </div>
                {h.status !== 'online' && <p className="text-xs text-red-600 mt-2">Host down — {((h.hyperv || {}).vms || []).length} dependent VM(s) impacted</p>}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-white border-slate-200">
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Building2 className="w-4 h-4 text-cyan-500" />Site Health</CardTitle></CardHeader>
          <CardContent className="space-y-1.5" data-testid="infra-site-health">
            {data.sites_health.length === 0 ? <p className="text-sm text-slate-500 py-2 text-center">No sites with monitored devices.</p> :
              data.sites_health.map((s) => (
                <div key={s.site_id} className="flex items-center justify-between text-sm p-2.5 rounded-lg border border-slate-200">
                  <div>
                    <span className="font-medium text-slate-800">{s.site_name || s.site_id}</span>
                    <span className="text-xs text-slate-500 ml-2">{s.customer_name}</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <span className="text-slate-500">{s.online}/{s.total} online</span>
                    {s.offline > 0 && <Badge className="bg-red-100 text-red-700">{s.offline} down</Badge>}
                    {s.open_alerts > 0 && <Badge className="bg-amber-100 text-amber-700">{s.open_alerts} alerts</Badge>}
                    {s.offline === 0 && s.open_alerts === 0 && <Badge className="bg-emerald-100 text-emerald-700">healthy</Badge>}
                  </div>
                </div>
              ))}
          </CardContent>
        </Card>
        <Card className="bg-white border-slate-200">
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><ShieldAlert className="w-4 h-4 text-red-500" />Certificate Warnings</CardTitle></CardHeader>
          <CardContent className="space-y-1.5" data-testid="infra-cert-warnings">
            {data.cert_warnings.length === 0 ? <p className="text-sm text-slate-500 py-2 text-center">All monitored certificates are healthy.</p> :
              data.cert_warnings.map((w) => (
                <div key={w.id} className="flex items-center justify-between text-sm p-2.5 rounded-lg border border-red-200 bg-red-50/40">
                  <span className="font-medium text-slate-800">{w.label || w.url}</span>
                  <Badge className={w.ssl_days_left <= 7 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}>expires in {w.ssl_days_left}d</Badge>
                </div>
              ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
